import {
  permissionSnapshotPartitionIndex,
  type SearchPartitionDigests,
  type SearchPartitionPage,
  type WorkspaceSearchAdvancedPayload,
} from '@streamlain/permission-snapshot-contract';
import { OnEvent, Service } from '@toeverything/infra';
import { firstValueFrom } from 'rxjs';

import {
  isBackendAvailableForImmediateRequest,
  subscribeBackendRecovery,
} from '../../../utils/backend-availability';
import { DeadlineTimer, RetryBackoff } from '../../../utils/deadline-timer';
import type { WorkspaceServerService } from '../../cloud';
import { workspaceApiFetch } from '../../cloud/services/workspace-api-client';
import type { DocRecord, DocsService } from '../../doc';
import type { WorkspaceLocalState } from '../../workspace';
import { WorkspaceInitialized } from '../../workspace/events';
import type { WorkspaceService } from '../../workspace/services/workspace';
import type { DocsSearchService } from './docs-search';
import {
  type AppliedSearchPartitionDigests,
  nextSearchPartitionReconcileAt,
  SearchPartitionReconciler,
} from './search-partition-reconciler';
import { planSearchSources, type SearchSourcePlan } from './search-source-plan';
import type { WarmCacheService } from './warm-cache';

// Version 2: partitions applied before carry no file names; one full fetch.
const APPLIED_DIGESTS_KEY = 'search-partition-digests:2';
const RECONCILED_AT_KEY = 'search-partition-reconciled-at';
/** Wait after a failed reconcile: 1 min, doubling up to 1 h. */
const RECONCILE_RETRY_BASE_MS = 60_000;
const RECONCILE_RETRY_MAX_MS = 60 * 60_000;

/**
 * Keeps the local search index of a cloud workspace equal to the server's:
 * when the device is idle (indexer queue empty, no battery saving) it
 * compares the partition digests and replaces only differing partitions,
 * at the latest after the safety-net time, and right after the connection
 * comes back, because search events may have been missed meanwhile. It
 * wakes at the due time instead of on a fixed tick, like the offline-access
 * scheduler of the permissions.
 */
@OnEvent(WorkspaceInitialized, service => service.onWorkspaceInitialized)
export class SearchPartitionSyncService extends Service {
  private readonly timer = new DeadlineTimer(() => this.check(false));
  private readonly retry = new RetryBackoff(
    RECONCILE_RETRY_BASE_MS,
    RECONCILE_RETRY_MAX_MS
  );
  private started = false;
  private disposed = false;
  private running = false;
  /** Indexer queue empty and no battery saving, from the indexer state. */
  private idle = false;
  private reconciler: SearchPartitionReconciler | null = null;
  /** Reconcile and event refreshes write one after the other. */
  private writes: Promise<unknown> = Promise.resolve();
  /** Last search version seen per page, to skip repeated events. */
  private readonly seenVersions = new Map<string, string>();
  /** The server's partition digests as last fetched; null until then. */
  private serverDigests: SearchPartitionDigests | null = null;

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly docsService: DocsService,
    private readonly warmCache: WarmCacheService,
    private readonly localState: WorkspaceLocalState,
    private readonly docsSearch: DocsSearchService
  ) {
    super();
  }

  onWorkspaceInitialized() {
    const workspace = this.workspaceService.workspace;
    const cloudMemberWorkspace =
      workspace.flavour !== 'local' &&
      !workspace.openOptions.isSharedMode &&
      !workspace.openOptions.externalDocAccess;
    if (!cloudMemberWorkspace || this.started) return;
    this.started = true;

    this.reconciler = this.createReconciler();
    const stateSubscription = workspace.engine.indexer.state$.subscribe(
      state => {
        const idle = state.indexing === 0 && !state.batterySaveMode;
        if (idle === this.idle) return;
        // Idle moves the due time forward; plan the wake-up again.
        this.idle = idle;
        this.check(false);
      }
    );
    const unsubscribeRecovery = subscribeBackendRecovery(() => {
      this.retry.reset();
      this.check(true);
    });
    const searchSubscription = workspace.engine.searchAdvanced$?.subscribe(
      event => this.onSearchAdvanced(event)
    );
    const permissionSubscription =
      workspace.engine.permissionsChanged$?.subscribe(event =>
        this.refresh(
          [
            ...(event.affectedDocIds ?? []),
            ...(event.docPlacementChanges ?? []).map(change => change.docId),
          ],
          true
        )
      );
    this.disposables.push(
      () => searchSubscription?.unsubscribe(),
      () => permissionSubscription?.unsubscribe(),
      () => stateSubscription.unsubscribe(),
      unsubscribeRecovery,
      () => {
        this.disposed = true;
        this.timer.clear();
      }
    );
    this.check(false);
    if (!this.running && this.workspaceServerService.server) {
      // Which partitions are current is known without waiting for a reconcile.
      this.enqueue(() =>
        this.fetchDigests().catch(error => {
          console.warn('search partition digests failed', error);
        })
      );
    }
  }

  /** Which source answers each page; null until the server's digests are known. */
  searchSources(): SearchSourcePlan | null {
    if (!this.serverDigests) return null;
    const docsMap = this.docsService.list.docsMap$.value;
    return planSearchSources({
      serverDigests: this.serverDigests,
      applied: this.readApplied(),
      docIds: docsMap.keys(),
      inWarmCache: docId => {
        const record = docsMap.get(docId);
        return !!record && this.inWarmCache(record);
      },
    });
  }

  /**
   * Runs the reconcile when due, otherwise wakes at the due time. Without a
   * connection nothing is planned: the recovery signal checks again.
   */
  private check(force: boolean) {
    // A running reconcile plans the next one when it ends.
    if (this.disposed || this.running) return;
    const now = Date.now();
    const dueAt = force
      ? now
      : Math.max(
          nextSearchPartitionReconcileAt({
            lastReconciledAt:
              this.localState.get<number>(RECONCILED_AT_KEY) ?? null,
            idle: this.idle,
          }),
          this.retry.nextAttemptAt
        );
    if (dueAt > now) {
      this.timer.schedule([dueAt], now);
      return;
    }
    this.timer.clear();
    const reconciler = this.reconciler;
    if (
      !reconciler ||
      !this.workspaceServerService.server ||
      !isBackendAvailableForImmediateRequest()
    ) {
      return;
    }

    this.running = true;
    this.enqueue(() =>
      reconciler
        .reconcile()
        .then(() => {
          this.localState.set(RECONCILED_AT_KEY, Date.now());
          this.retry.reset();
        })
        .catch(error => {
          // Tried again after the back-off; nothing local was dropped.
          console.warn('search partition reconcile failed', error);
          this.retry.failed(Date.now());
        })
        .finally(() => {
          this.running = false;
          this.check(false);
        })
    );
  }

  private onSearchAdvanced(event: WorkspaceSearchAdvancedPayload) {
    const version = `${event.contentVersion ?? ''}\t${event.fileTextVersion ?? ''}`;
    const key = event.messageId
      ? `${event.docId}\u0000${event.messageId}`
      : event.docId;
    if (!event.accessChanged && this.seenVersions.get(key) === version) {
      return;
    }
    this.seenVersions.set(key, version);
    if (event.messageId) this.refreshMail(event, event.messageId);
    else this.refresh([event.docId]);
  }

  /** Mails follow the server regardless of the warm cache. */
  private refreshMail(
    event: WorkspaceSearchAdvancedPayload,
    messageId: string
  ) {
    const reconciler = this.reconciler;
    if (!reconciler) return;
    this.enqueue(() =>
      reconciler
        .refreshMails([
          { docId: event.docId, messageId, contentHash: event.contentVersion },
        ])
        .catch(error => {
          // The next reconcile catches up; nothing local was dropped.
          console.warn('search mail refresh failed', error);
        })
    );
  }

  /**
   * Loads the server's search content of event pages in the warm cache; a page
   * outside it keeps its catalog row only and costs no request. After a
   * permission change every page is checked for access, because a mailbox
   * page outside the warm cache still holds its mails.
   */
  private refresh(docIds: readonly string[], accessChanged = false) {
    const reconciler = this.reconciler;
    if (!reconciler) return;
    const docsMap = this.docsService.list.docsMap$.value;
    const warmCacheDocIds: string[] = [];
    const checkedDocIds: string[] = [];
    for (const docId of docIds) {
      const record = docsMap.get(docId);
      if (record && this.inWarmCache(record)) warmCacheDocIds.push(docId);
      else if (accessChanged) checkedDocIds.push(docId);
    }
    if (warmCacheDocIds.length === 0 && checkedDocIds.length === 0) return;
    this.enqueue(() =>
      reconciler.refreshDocs(warmCacheDocIds, checkedDocIds).catch(error => {
        // The next reconcile catches up; nothing local was dropped.
        console.warn('search page refresh failed', error);
      })
    );
  }

  private inWarmCache(record: DocRecord) {
    return this.warmCache.includes(record.id);
  }

  private enqueue(write: () => Promise<unknown>) {
    this.writes = this.writes.then(write);
  }

  private readApplied() {
    return (
      this.localState.get<AppliedSearchPartitionDigests>(APPLIED_DIGESTS_KEY) ??
      {}
    );
  }

  private get partitionsUrl() {
    const workspaceId = this.workspaceService.workspace.id;
    return `/api/workspaces/${encodeURIComponent(workspaceId)}/search/partitions`;
  }

  private get requestPolicy() {
    return {
      workspaceId: this.workspaceService.workspace.id,
      category: 'workspace-api',
      mode: 'background',
      priority: 'background',
    } as const;
  }

  private async fetchDigests() {
    const response = await workspaceApiFetch(
      this.partitionsUrl,
      { credentials: 'include' },
      { ...this.requestPolicy, key: 'search-partitions' }
    );
    const digests = (await response.json()) as SearchPartitionDigests;
    this.serverDigests = digests;
    return digests;
  }

  private createReconciler() {
    const base = this.partitionsUrl;
    const policy = this.requestPolicy;
    return new SearchPartitionReconciler({
      fetchDigests: () => this.fetchDigests(),
      fetchPage: async (
        partitionIndex,
        docIds,
        cursor,
        messageIds = [],
        checkedDocIds
      ) => {
        const query = new URLSearchParams({ docs: docIds.join(',') });
        if (messageIds.length > 0) query.set('mails', messageIds.join(','));
        if (checkedDocIds) {
          query.set('refresh', '1');
          if (checkedDocIds.length > 0)
            query.set('checked', checkedDocIds.join(','));
        }
        if (cursor) query.set('cursor', cursor);
        const response = await workspaceApiFetch(
          `${base}/${partitionIndex}?${query.toString()}`,
          { credentials: 'include' },
          { ...policy, key: `search-partition:${partitionIndex}` }
        );
        return (await response.json()) as SearchPartitionPage;
      },
      warmCacheDocIdsByPartition: () => {
        const byPartition = new Map<number, string[]>();
        for (const record of this.docsService.list.docs$.value) {
          if (!this.inWarmCache(record)) continue;
          const partition = permissionSnapshotPartitionIndex('doc', record.id);
          const docIds = byPartition.get(partition);
          if (docIds) docIds.push(record.id);
          else byPartition.set(partition, [record.id]);
        }
        return byPartition;
      },
      writePages: pages =>
        this.workspaceService.workspace.engine.indexer.writeServerSnapshot(
          pages
        ),
      removePages: docIds => {
        this.workspaceService.workspace.engine.indexer.removeFromIndex(docIds);
        this.warmCache.forget(docIds);
      },
      readLocalMails: async () => {
        const nodes = await firstValueFrom(this.docsSearch.mails$());
        return nodes.map(node => ({
          docId: firstField(node.fields.docId),
          messageId: firstField(node.fields.messageId),
          contentHash: firstField(node.fields.contentHash),
        }));
      },
      writeMails: changes =>
        this.workspaceService.workspace.engine.indexer.writeServerMails(
          changes
        ),
      writeFileNames: changes =>
        this.workspaceService.workspace.engine.indexer.writeServerFileNames(
          changes
        ),
      readApplied: () => this.readApplied(),
      writeApplied: applied =>
        this.localState.set(APPLIED_DIGESTS_KEY, applied),
    });
  }
}

function firstField(value: string | string[] | undefined) {
  return (Array.isArray(value) ? value[0] : value) ?? '';
}
